import React, { useState, useRef, useEffect } from "react";
import { Loader2, Bot, User, BookOpen, MessageSquare, X } from "lucide-react";
import { cn } from "../../lib/utils";

function Whiteboard({ content, isLoading }) {
  return (
    <div className="w-full h-full bg-[var(--surface)] rounded-lg border border-[var(--bd)] p-4 relative overflow-y-auto">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--surface)]/80 backdrop-blur-sm z-10">
          <div className="flex flex-col items-center gap-4 text-center px-4">
            <Loader2 className="w-10 h-10 animate-spin text-[var(--accent)]" />
            <p className="text-[var(--t2)] font-medium">Your lesson is being prepared...</p>
          </div>
        </div>
      )}
      {!isLoading && !content && (
        <div className="flex items-center justify-center h-full">
          <p className="text-[var(--t2)] text-center text-sm">
            Ask Cortex to explain a topic, e.g. "Teach me about 3-phase power" or "What is power factor?"
          </p>
        </div>
      )}
      {content && (
        <div className="text-[var(--t1)] text-sm leading-relaxed whitespace-pre-wrap">
          {content}
        </div>
      )}
    </div>
  );
}

export default function RoboSidePanel({
  isOpen,
  onClose,
  isGenerating,
  isThinking,
  storyContent,
  onConversation,
  conversationHistory,
}) {
  const [userInput, setUserInput] = useState("");
  const [activeTab, setActiveTab] = useState("chat");
  const scrollRef = useRef(null);

  const handleAsk = () => {
    if (userInput.trim()) {
      onConversation(userInput);
      setUserInput("");
    }
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [conversationHistory]);

  return (
    <aside
      className={cn(
        "flex flex-col bg-[var(--surface)] border-l border-[var(--bd)] transition-all duration-300 ease-in-out overflow-hidden",
        isOpen ? "w-full max-w-sm" : "w-0"
      )}
    >
      <div className={cn("flex flex-col h-full", !isOpen && "invisible")}>
        {onClose && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--bd)] md:hidden">
            <span className="font-heading font-bold text-sm">Chat & Whiteboard</span>
            <button onClick={onClose} className="w-8 h-8 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--accent)]">
              <X size={16} />
            </button>
          </div>
        )}
        <div className="flex border-b border-[var(--bd)] m-2 rounded-lg bg-[var(--surface2)] p-1 gap-1">
          <button
            onClick={() => setActiveTab("chat")}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all",
              activeTab === "chat" ? "bg-[var(--surface)] text-[var(--t1)] shadow-sm" : "text-[var(--t2)] hover:text-[var(--t1)]"
            )}
          >
            <MessageSquare size={14} /> Chat
          </button>
          <button
            onClick={() => setActiveTab("whiteboard")}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-all",
              activeTab === "whiteboard" ? "bg-[var(--surface)] text-[var(--t1)] shadow-sm" : "text-[var(--t2)] hover:text-[var(--t1)]"
            )}
          >
            <BookOpen size={14} /> Whiteboard
          </button>
        </div>

        {activeTab === "chat" && (
          <div className="flex-1 flex flex-col gap-4 p-4 pt-0 overflow-hidden">
            <div className="flex-1 min-h-0 overflow-y-auto pr-2 space-y-4" ref={scrollRef}>
              {conversationHistory.map((entry, index) => (
                <div key={index} className={cn("flex items-start gap-3", entry.role === "user" ? "justify-end" : "justify-start")}>
                  {entry.role === "model" && (
                    <div className="w-8 h-8 rounded-md bg-[var(--accent-soft)] flex items-center justify-center shrink-0">
                      <Bot size={16} className="text-[var(--accent)]" />
                    </div>
                  )}
                  <div className={cn("rounded-lg px-3 py-2 max-w-[85%] text-sm leading-relaxed", entry.role === "user" ? "bg-[var(--accent)] text-black" : "bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t1)]")}>
                    <p className="whitespace-pre-wrap">{entry.text}</p>
                  </div>
                  {entry.role === "user" && (
                    <div className="w-8 h-8 rounded-md bg-[var(--surface2)] flex items-center justify-center shrink-0">
                      <User size={16} className="text-[var(--t2)]" />
                    </div>
                  )}
                </div>
              ))}
              {isThinking && (
                <div className="flex items-start gap-3 justify-start">
                  <div className="w-8 h-8 rounded-md bg-[var(--accent-soft)] flex items-center justify-center shrink-0">
                    <Bot size={16} className="text-[var(--accent)]" />
                  </div>
                  <div className="rounded-lg px-3 py-2 bg-[var(--surface2)] border border-[var(--bd)] flex items-center">
                    <Loader2 className="w-4 h-4 animate-spin text-[var(--accent)]" />
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Ask a question..."
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAsk()}
                disabled={isThinking}
                className="flex-1 bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2.5 text-sm text-[var(--t1)] focus:outline-none focus:border-[var(--accent)] transition-colors"
              />
              <button
                onClick={handleAsk}
                disabled={isThinking || !userInput.trim()}
                className="px-4 py-2.5 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform disabled:opacity-40"
              >
                {isThinking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}
              </button>
            </div>
          </div>
        )}

        {activeTab === "whiteboard" && (
          <div className="flex-1 flex flex-col gap-4 p-4 pt-0 overflow-y-auto">
            <div className="flex-1 min-h-0">
              <Whiteboard content={storyContent} isLoading={isGenerating} />
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
